import {
	getAppleMapsDirectionsUrl,
	getGoogleMapsDirectionsUrl,
} from '#app/utils/maps.ts'

/**
 * The tickets that hold a seat, as a Prisma ticket `where`: every ticket
 * (held in a cart, paid, or a test purchase) except a refunded one. Refunded
 * means cancelled: the ticket is void and its seat is free for someone else.
 */
export const SEAT_HOLDING_TICKETS = {
	paymentStatus: { not: 'REFUNDED' },
} as const

/** The `_count` select for seat availability: `_count.tickets` = seats taken */
export const SEATS_TAKEN_COUNT = {
	select: { tickets: { where: SEAT_HOLDING_TICKETS } },
} as const

/** Whether a ticket row holds a seat; see `SEAT_HOLDING_TICKETS` */
export const holdsSeat = (ticket: { paymentStatus?: string | null }) =>
	ticket.paymentStatus !== 'REFUNDED'

/**
 * What seat availability needs. Pages get `ticketCount` (a Prisma `_count`
 * of `SEATS_TAKEN_COUNT`), never ticket rows: each row carries the holder's
 * name and the door `hash` behind /t/:hash. `tickets` is the fallback for
 * callers that already hold the rows server-side.
 */
export type SeatAvailabilityInput = {
	seats?: { total: number } | null
	ticketCount?: number | null
	tickets?: ReadonlyArray<{ paymentStatus?: string | null }> | null
}

export const seatsTaken = (event: SeatAvailabilityInput): number =>
	event.ticketCount ?? event.tickets?.filter(holdsSeat).length ?? 0

// No seats row means availability is unknown. A total of 0 is no seats at
// all: the sale refuses every ticket ("Ingen plassar igjen"), so the pages say
// "Utseld" rather than offer a picker that can't sell.
export const seatsRemaining = (event: SeatAvailabilityInput): number | null => {
	const total = event.seats?.total
	if (total == null) return null
	return Math.max(0, total - seatsTaken(event))
}

export const isSoldOut = (event: SeatAvailabilityInput): boolean =>
	seatsRemaining(event) === 0

/**
 * The "Tal på plassar" field of the event form. Empty is an error, not "keep
 * what was there": a concert that sells needs a number, and 0 is sold out.
 */
export function parseSeatsInput(
	value: FormDataEntryValue | null,
): { seats: number } | { error: string } {
	if (typeof value !== 'string' || value.trim() === '') {
		return { error: 'Tal på plassar må fyllast ut' }
	}
	const seats = Number(value)
	if (!Number.isInteger(seats) || seats < 0) {
		return { error: 'Talet på plassar må vere eit heilt tal på 0 eller meir' }
	}
	return { seats }
}

/** The seats the form's preview card shows: none until the field holds one. */
export const previewSeats = (value: string) => {
	const parsed = parseSeatsInput(value)
	return 'seats' in parsed ? { total: parsed.seats } : null
}

export type MapsPlatform = 'ios' | 'android'

export const getMapsDirectionsUrl = (
	location: string,
	platform: MapsPlatform,
) =>
	platform === 'ios'
		? getAppleMapsDirectionsUrl(location)
		: getGoogleMapsDirectionsUrl(location)
